"use client"
import { toast } from "@/components/Toast"
import { IconButton, Spinner, Text } from "@/elements"
import { Picture } from "@/icons/outline"
import { XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import Image from "next/image"
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react"
import { isFileValid, VALID_FILETYPES } from "@/utils/isFileValid"

interface DropzoneInputProps {
  onSelectFile: (files: File[]) => void
  multiple?: boolean
  allowVideos?: boolean
  imagePreview?: string | null
  onRemovePreview?: () => void
  className?: string
  loading?: boolean
  dropzoneDisabled?: boolean
}

export const DropzoneInput = forwardRef(
  (
    {
      onSelectFile,
      multiple = false,
      imagePreview,
      onRemovePreview,
      className = "",
      loading = false,
      allowVideos = false,
      dropzoneDisabled,
    }: DropzoneInputProps,
    ref
  ) => {
    const dropzoneRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Expose a method to the parent using `useImperativeHandle`
    useImperativeHandle(ref, () => ({
      openFileDialog: () => {
        inputRef.current?.click()
      },
    }))

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (dropzoneRef.current) {
        dropzoneRef.current.classList.add("border-bg-4")
      }
    }, [])

    const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (dropzoneRef.current) {
        dropzoneRef.current.classList.remove("border-bg-4")
      }
    }, [])

    const handleFiles = (initialFiles: File[]) => {
      const files = initialFiles.filter(file => {
        if (!allowVideos && file.type.startsWith("/video")) {
          toast.error("You cannot upload a video file here.")
          return false
        }
        return isFileValid({ file })
      })

      if (files.length > 0) {
        onSelectFile(files)
      }
    }

    const onDrop = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()

        handleFiles(Array.from(e.dataTransfer.files))
      },
      [onSelectFile, allowVideos]
    )

    return (
      <div
        ref={dropzoneRef}
        className={clsx(
          "relative flex min-h-[236px] cursor-pointer items-center justify-center rounded-md border border-dashed border-bg-3 transition-colors hover:border-bg-4",
          className
        )}
        onDragOver={dropzoneDisabled ? undefined : onDragOver}
        onDragLeave={dropzoneDisabled ? undefined : onDragLeave}
        onDrop={dropzoneDisabled ? undefined : onDrop}>
        {loading ? (
          <div className="relative flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        ) : imagePreview ? (
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative size-[100px]">
              <div className="relative size-[100px] overflow-hidden rounded-md">
                <Image src={imagePreview} alt="Cover image" className="select-none object-cover" fill loading="lazy" />
              </div>
              {!!onRemovePreview && (
                <div
                  className="absolute right-[-8px] top-[-8px]"
                  style={{
                    zIndex: 1,
                  }}>
                  <IconButton
                    size="xs"
                    floating
                    Icon={XMarkIcon}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      onRemovePreview()
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <Picture className="size-8 stroke-fg-1" />
            <Text className="mt-4 hidden sm:block">{allowVideos ? "Drag photo or video here" : "Drag photo here"}</Text>
            <Text variant="body2" className="mt-6 underline underline-offset-4">
              Upload from your device
            </Text>
          </div>
        )}

        <input
          id="file-upload"
          type="file"
          ref={inputRef}
          className="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0"
          accept={
            allowVideos
              ? VALID_FILETYPES.join(",")
              : VALID_FILETYPES.filter(type => !type.startsWith("video/")).join(",")
          }
          onClick={e => {
            e.stopPropagation()
          }}
          multiple={multiple}
          onChange={e => {
            handleFiles(Array.from(e.target.files || []))
          }}
        />
      </div>
    )
  }
)

DropzoneInput.displayName = "DropzoneInput"
